import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";

import { Table, Container, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import { ADMIN_COURSE_API } from "../../../api/api";

import { FaEye, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { RiArrowDownSLine } from "react-icons/ri";

import { exportToExcel } from "../../../utils/exportExcel";
import { handleApiError } from "../../../utils/apiErrorHandler";
import { useSortableTable } from "../../../hooks/useSortableTable";

import excelImg from "../../../assets/excel-icon.png";
import Nofound from "../../../assets/not-found.png";

import "./AdminAllCourseManagement.css";

const AdminAllCourseManagement = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);

  // Filter states
  const [selectedCourseFilter, setSelectedCourseFilter] =
    useState("All Courses");
  const [selectedStatusFilter, setSelectedStatusFilter] =
    useState("All Status");

  //loading
  const [loadingAllCourses, setLoadingAllCourses] = useState(true);

  // main toast error
  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      setLoadingAllCourses(false);
      navigate("/admin-signin");
      return;
    }

    const fetchCourses = async () => {
      try {
        const response = await axios.get(
          ADMIN_COURSE_API.GET_ADMIN_COURSE_LIST,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const data = response.data;

        if (data.statusCode === 200) {
          setCourses(data.data || []);
        } else {
          // This will handle any 2xx response that is not 201
          showErrorToast(data.message);
        }
      } catch (error) {
        console.error("Error fetching courses:", error);
        // Server responded with non-2xx status (like 400, 500)

        // CHECK (expiry / invalid token)
        handleApiError(error, navigate, showErrorToast, "/admin-signin");
      } finally {
        setLoadingAllCourses(false);
      }
    };

    fetchCourses();
  }, [navigate]);

  const handleEyeClick = (course) => {
    const { status, course_id } = course;
    if (course.modules === "0") return;

    switch (status?.toLowerCase()) {
      case "published":
        navigate(`/header/admin/course-published/${course_id}`);
        break;
      case "pending":
        navigate(`/header/admin/course-pending/${course_id}`);
        break;
      case "rejected":
        navigate(`/header/admin/course-rejected/${course_id}`);
        break;
      default:
        break;
    }
  };

  // Dynamic dropdown options
  const filteredCoursesForDropdown = useMemo(
    () => [
      "All Courses",
      ...new Set(courses.map((c) => (c.course || "").trim())),
    ],
    [courses],
  );

  const filteredStatusForDropdown = useMemo(() => {
    let filtered = courses;

    if (selectedCourseFilter !== "All Courses") {
      filtered = courses.filter(
        (c) => (c.course || "").trim() === selectedCourseFilter.trim(),
      );
    }

    return [
      "All Status",
      ...new Set(filtered.map((c) => c.status?.trim()).filter(Boolean)),
    ];
  }, [courses, selectedCourseFilter]);

  // Filtered courses based on selection explicit return
  const filteredCourses = useMemo(() => {
    return courses.filter((course) => {
      const courseName = (course.course || "").trim();
      const courseStatus = (course.status || "").toLowerCase();

      const matchesCourse =
        selectedCourseFilter === "All Courses" ||
        courseName === selectedCourseFilter.trim();

      const matchesStatus =
        selectedStatusFilter === "All Status" ||
        courseStatus === selectedStatusFilter.toLowerCase();

      return matchesCourse && matchesStatus;
    });
  }, [courses, selectedCourseFilter, selectedStatusFilter]);

  // SORT HOOK (MAIN ADDITION)
  const { sortedData, sortConfig, handleSort } = useSortableTable(
    filteredCourses,
    "course",
  );

  const handleExportCourses = () => {
    // Format data for Excel
    const formattedData = sortedData.map((course) => ({
      Course: course.course?.trim() || "N/A",
      Modules: course.modules || "0",
      Category: course.category || "N/A",
      Tutor: course.tutor || "N/A",
      Level: course.level || "N/A",
      Students: course.students || "0",
      Status: course.status || "N/A",
    }));

    exportToExcel({
      data: formattedData,
      fileName: "Courses",
      sheetName: "Courses",
    });
  };

  //  SORT ICON HELPER
  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort size={11} />;
    if (sortConfig.direction === "asc") return <FaSortUp size={11} />;
    return <FaSortDown size={11} />;
  };

  return (
    <Container className="admin-all-courses-section">
      <h4 className="admin-dashboard-title">Course Management</h4>
      <p className="admin-dashboard-subtitle">
        Manage courses, tutor assignments, and learner enrollment details
      </p>

      <div className="admin-all-courses-table-scroll-wrapper">
        <div className="admin-all-courses-header">
          <div className="admin-all-courses-header-left">
            <h5 className="admin-all-courses-header-title">
              All Courses{" "}
              <span className="admin-all-courses-count">
                ({sortedData.length})
              </span>
            </h5>
          </div>

          <div className="admin-all-courses-header-right">
            <div className="admin-course-filter-wrapper">
              <select
                className="admin-course-filter-select"
                value={selectedCourseFilter}
                onChange={(e) => {
                  setSelectedCourseFilter(e.target.value);
                  // setSelectedStatusFilter("All Status");
                }}
              >
                {filteredCoursesForDropdown.map((course, i) => (
                  <option key={i} value={course}>
                    {course}
                  </option>
                ))}
              </select>
              <RiArrowDownSLine className="admin-status-filter-select-arrow" />
            </div>

            <div className="admin-status-filter-wrapper">
              <select
                className="admin-course-filter-select"
                value={selectedStatusFilter}
                onChange={(e) => setSelectedStatusFilter(e.target.value)}
              >
                {filteredStatusForDropdown.map((status, i) => (
                  <option key={i} value={status}>
                    {status}
                  </option>
                ))}
              </select>
              <RiArrowDownSLine className="admin-status-filter-select-arrow" />
            </div>

            <Button
              size="sm"
              className="admin-dashboard-table-export"
              onClick={handleExportCourses}
              disabled={sortedData.length === 0}
              title={
                sortedData.length === 0
                  ? "No data available to export"
                  : "Export to Excel"
              }
              style={{
                cursor: sortedData.length === 0 ? "not-allowed" : "pointer",
                opacity: sortedData.length === 0 ? 0.5 : 1,
              }}
            >
              <img
                src={excelImg}
                alt="Excel"
                className="admin-dashboard-table-export-excel-img"
              />
            </Button>
          </div>
        </div>

        <Table className="admin-all-courses-table">
          <thead>
            <tr>
              <th
                onClick={() => handleSort("course")}
                style={{ cursor: "pointer" }}
              >
                Course {renderSortIcon("course")}
              </th>

              <th
                onClick={() => handleSort("modules")}
                style={{ cursor: "pointer" }}
              >
                Modules {renderSortIcon("modules")}
              </th>

              <th
                onClick={() => handleSort("category")}
                style={{ cursor: "pointer" }}
              >
                Category {renderSortIcon("category")}
              </th>

              <th
                onClick={() => handleSort("tutor")}
                style={{ cursor: "pointer" }}
              >
                Tutor {renderSortIcon("tutor")}
              </th>

              <th
                onClick={() => handleSort("level")}
                style={{ cursor: "pointer" }}
              >
                Level {renderSortIcon("level")}
              </th>

              <th
                onClick={() => handleSort("students")}
                style={{ cursor: "pointer" }}
              >
                Students {renderSortIcon("students")}
              </th>

              <th
                onClick={() => handleSort("status")}
                style={{ cursor: "pointer" }}
              >
                Status {renderSortIcon("status")}
              </th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {loadingAllCourses ? (
              <tr>
                <td colSpan="8" style={{ textAlign: "center" }}>
                  Loading courses...
                </td>
              </tr>
            ) : sortedData.length > 0 ? (
              sortedData.map((course) => (
                <tr key={course.course_id}>
                  <td
                    className="admin-all-courses-table-td"
                    title={course.course}
                  >
                    {course.course?.trim() || "-"}
                  </td>
                  <td>
   
                    {Number(course.modules) > 0
                      ? `${course.modules} modules`
                      : "-"}
                  </td>
                  <td>
                    <span className="admin-all-courses-category-link">
                      {course.category || "-"}
                    </span>
                  </td>
                  <td
                    className="admin-all-courses-table-td"
                    title={course.tutor}
                  >
                    {course.tutor || "-"}
                  </td>
                  <td>
                    <span
                      className={`admin-all-courses-level ${course.level?.toLowerCase()}`}
                    >
                      {course.level || "-"}
                    </span>
                  </td>
                  <td>
                    {" "}
                    {Number(course.students) > 0 ? course.students : "-"}
                  </td>
                  <td>
                    <span
                      className={`admin-all-courses-status ${course.status?.toLowerCase()}`}
                    >
                      {course.status || "N/A"}
                    </span>
                  </td>
                  <td className="admin-all-courses-section-eye">
                    <FaEye
                      onClick={() => handleEyeClick(course)}
                      style={{
                        cursor:
                          course.modules !== "0" ? "pointer" : "not-allowed",
                        opacity: course.modules !== "0" ? 1 : 0.5,
                      }}
                      title={
                        course.modules !== "0"
                          ? "View Course"
                          : "No modules added yet"
                      }
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="admin-all-courses-empty-table">
                  <img
                    src={Nofound}
                    alt="No Data Found"
                    className="admin-all-tutor-empty-img"
                  />
                  <p>No Data Found</p>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </Container>
  );
};

export default AdminAllCourseManagement;
